import React from "react";
import { AlertTriangle, ChevronDown, X } from "lucide-react";
import FieldLabel from "../../ui/FieldLabel";
import MutedText from "../../ui/MutedText";
import TransformDisplayModeSwitches from "../../property-panels/TransformDisplayModeSwitches";
import PhysicsAuthoringLockNotice from "../../ui/PhysicsAuthoringLockNotice";
import { ACTUATOR_CONTROL_SECTION_INFO } from "../../../utils/actuatorHelpText";
import SectionHeading from "./SectionHeading";
import {
  ActuatorMotorControlRow,
  ActuatorPositionControlRow,
  JointPositionControlRow,
} from "./ActuatorControlRows";
import { JOINT_TAB_PHYSICS_ON_NOTICE, PANEL_MODES } from "./constants";

const CloseButton = ({ className, onClose }) => (
  <button
    type="button"
    onClick={onClose}
    className={className}
    style={{ backgroundColor: "transparent", border: "none", outline: "none" }}
    aria-label="Close actuator controls"
  >
    <X className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
  </button>
);

const GroupHeader = ({
  collapsedGroups,
  collapseLabel,
  count,
  firstVisibleGroupKeyForMobile,
  group,
  groupCollapseChevronButtonClass,
  groupHeaderRowClass,
  isMobileLayout,
  onClose,
  setCollapsedGroups,
}) => {
  const groupCollapsed = collapsedGroups[group.key] === true;

  return (
    <div className={groupHeaderRowClass}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          className={`font-semibold text-gray-100 truncate ${isMobileLayout ? "text-xs" : "text-sm"}`}
          id={
            isMobileLayout && group.key === firstVisibleGroupKeyForMobile
              ? "actuator-controls-mobile-title"
              : undefined
          }
        >
          {group.title} <FieldLabel as="span">({count})</FieldLabel>
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {!isMobileLayout ? (
          <button
            type="button"
            className={groupCollapseChevronButtonClass}
            onClick={() =>
              setCollapsedGroups((prev) => ({
                ...prev,
                [group.key]: !(prev[group.key] === true),
              }))
            }
            title={groupCollapsed ? `Expand ${collapseLabel}` : `Minimize ${collapseLabel}`}
            aria-label={groupCollapsed ? `Expand ${collapseLabel}` : `Minimize ${collapseLabel}`}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${groupCollapsed ? "-rotate-90" : ""}`}
            />
          </button>
        ) : null}
        {isMobileLayout && group.key === firstVisibleGroupKeyForMobile ? (
          <CloseButton
            onClose={onClose}
            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-white outline-none focus:outline-none focus-visible:outline-none"
          />
        ) : null}
      </div>
    </div>
  );
};

const ActuatorControlsPanelBody = ({
  actuatorList,
  collapsedGroups,
  displayedPanelMode,
  effectivePanelMode,
  firstVisibleGroupKeyForMobile,
  groupedActuators,
  groupedJoints,
  isMobileLayout,
  isRunning,
  joints,
  motorGain,
  motorValues,
  onClose,
  panelMode,
  posValues,
  sceneActions,
  selectedJoint,
  setActuatorMotorControl,
  setActuatorPositionTarget,
  setCollapsedGroups,
  setPanelMode,
  worker,
}) => {
  const sliderGridListClass = isMobileLayout
    ? "grid grid-cols-3 gap-x-1 gap-y-0 px-1 pb-0"
    : "space-y-3 px-3 pr-1";
  const groupShellClass = isMobileLayout
    ? "rounded-lg bg-transparent"
    : "border border-gray-700 rounded bg-gray-900/30";
  const groupHeaderRowClass = isMobileLayout
    ? "flex items-center justify-between px-2 py-1"
    : "flex items-center justify-between px-3 py-2 border-b border-gray-800";
  const groupCollapseChevronButtonClass = isMobileLayout
    ? "h-6 w-6 flex items-center justify-center rounded text-gray-300 shrink-0"
    : "h-6 w-6 flex items-center justify-center rounded border border-gray-700 text-gray-300 hover:bg-gray-800 shrink-0";
  const warningBannerClass =
    "flex items-start gap-2 rounded border border-yellow-600/50 bg-yellow-900/20 px-3 py-2 text-yellow-300 text-xs";
  const visibleJointCount = React.useMemo(
    () => groupedJoints.reduce((sum, group) => sum + group.joints.length, 0),
    [groupedJoints]
  );
  const visibleActuatorCount = React.useMemo(
    () =>
      groupedActuators.reduce(
        (sum, group) => sum + group.motors.length + group.positions.length,
        0
      ),
    [groupedActuators]
  );
  const hasVisibleControls =
    effectivePanelMode === PANEL_MODES.JOINTS ? visibleJointCount > 0 : visibleActuatorCount > 0;
  const emptyTitle =
    effectivePanelMode === PANEL_MODES.JOINTS ? "No joints yet" : "No actuators yet";
  const emptyText =
    effectivePanelMode === PANEL_MODES.JOINTS
      ? "Add a robot or create hinge and slide joints to control them here."
      : "Add actuators to hinge, slide, or tendon-driven joints to control them here.";
  const modeValue = displayedPanelMode ?? panelMode;

  if (!hasVisibleControls) {
    return (
      <>
        {!isMobileLayout ? (
          <PanelModeControls
            displayedPanelMode={modeValue}
            isRunning={isRunning}
            setPanelMode={setPanelMode}
            warningBannerClass={warningBannerClass}
          />
        ) : null}
        <div
          className={
            isMobileLayout
              ? "mx-2 my-1 rounded-lg border border-dashed border-gray-700 px-3 py-4 text-center"
              : "rounded-md border border-dashed border-gray-700 px-4 py-8 text-center"
          }
        >
          <div className="text-sm font-medium text-gray-300">{emptyTitle}</div>
          <MutedText className="mt-1 text-xs">{emptyText}</MutedText>
        </div>
      </>
    );
  }

  return (
    <>
      {!isMobileLayout ? (
        <PanelModeControls
          displayedPanelMode={modeValue}
          isRunning={isRunning}
          setPanelMode={setPanelMode}
          warningBannerClass={warningBannerClass}
        />
      ) : null}
      {effectivePanelMode === PANEL_MODES.JOINTS ? (
        <div className={isMobileLayout ? "space-y-0.5" : "space-y-4"}>
          {groupedJoints.map((group) => {
            const totalJoints = group.joints.length;
            if (totalJoints === 0 && group.key === "unassigned") return null;
            const groupCollapsed = collapsedGroups[group.key] === true;
            return (
              <div key={group.key} className={groupShellClass}>
                <GroupHeader
                  collapsedGroups={collapsedGroups}
                  collapseLabel="joints"
                  count={totalJoints}
                  firstVisibleGroupKeyForMobile={firstVisibleGroupKeyForMobile}
                  group={group}
                  groupCollapseChevronButtonClass={groupCollapseChevronButtonClass}
                  groupHeaderRowClass={groupHeaderRowClass}
                  isMobileLayout={isMobileLayout}
                  onClose={onClose}
                  setCollapsedGroups={setCollapsedGroups}
                />
                {(isMobileLayout || !groupCollapsed) && (
                  <div className={isMobileLayout ? "py-0.5" : "space-y-4 py-3"}>
                    <div className={isMobileLayout ? "space-y-0" : "space-y-2"}>
                      {!isMobileLayout ? (
                        <SectionHeading
                          count={totalJoints}
                          tooltip={ACTUATOR_CONTROL_SECTION_INFO.joints}
                        >
                          Current Joint Positions
                        </SectionHeading>
                      ) : null}
                      {totalJoints > 0 && (
                        <div className={sliderGridListClass}>
                          {group.joints.map((entry) => (
                            <JointPositionControlRow
                              key={entry.jointUuid || entry.joint?.uuid || entry.label}
                              {...entry}
                              isMobileLayout={isMobileLayout}
                              isRunning={isRunning}
                              sceneActions={sceneActions}
                              selectedJoint={selectedJoint}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={isMobileLayout ? "space-y-0.5" : "space-y-4"}>
          {groupedActuators.map((group) => {
            const totalActuators = group.motors.length + group.positions.length;
            if (totalActuators === 0 && group.key === "unassigned") return null;
            const groupCollapsed = collapsedGroups[group.key] === true;
            return (
              <div key={group.key} className={groupShellClass}>
                <GroupHeader
                  collapsedGroups={collapsedGroups}
                  collapseLabel="actuators"
                  count={totalActuators}
                  firstVisibleGroupKeyForMobile={firstVisibleGroupKeyForMobile}
                  group={group}
                  groupCollapseChevronButtonClass={groupCollapseChevronButtonClass}
                  groupHeaderRowClass={groupHeaderRowClass}
                  isMobileLayout={isMobileLayout}
                  onClose={onClose}
                  setCollapsedGroups={setCollapsedGroups}
                />
                {(isMobileLayout || !groupCollapsed) && (
                  <div className={isMobileLayout ? "space-y-0 py-0.5" : "space-y-4 py-3"}>
                    {group.motors.length > 0 ? (
                      <div className={isMobileLayout ? "space-y-0" : "space-y-2"}>
                        {!isMobileLayout ? (
                          <SectionHeading
                            count={group.motors.length}
                            tooltip={ACTUATOR_CONTROL_SECTION_INFO.motors}
                          >
                            Motor / Speed Actuators
                          </SectionHeading>
                        ) : null}
                        <div className={sliderGridListClass}>
                          {group.motors.map((entry) => (
                            <ActuatorMotorControlRow
                              key={entry.actuator?.uuid || entry.controlName}
                              {...entry}
                              actuatorList={actuatorList}
                              isMobileLayout={isMobileLayout}
                              isRunning={isRunning}
                              motorGain={motorGain}
                              motorValues={motorValues}
                              setActuatorMotorControl={setActuatorMotorControl}
                              worker={worker}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {group.positions.length > 0 ? (
                      <div className={isMobileLayout ? "space-y-0" : "space-y-2"}>
                        {!isMobileLayout ? (
                          <SectionHeading
                            count={group.positions.length}
                            tooltip={ACTUATOR_CONTROL_SECTION_INFO.positions}
                          >
                            Position Targets
                          </SectionHeading>
                        ) : null}
                        <div className={sliderGridListClass}>
                          {group.positions.map((entry) => (
                            <ActuatorPositionControlRow
                              key={entry.actuator?.uuid || entry.controlName}
                              {...entry}
                              actuatorList={actuatorList}
                              isMobileLayout={isMobileLayout}
                              isRunning={isRunning}
                              joints={joints}
                              posValues={posValues}
                              sceneActions={sceneActions}
                              setActuatorPositionTarget={setActuatorPositionTarget}
                              worker={worker}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const PanelModeControls = ({ displayedPanelMode, isRunning, setPanelMode, warningBannerClass }) => (
  <>
    <div className="flex items-center justify-between gap-2">
      <FieldLabel>Control Type</FieldLabel>
      <TransformDisplayModeSwitches
        value={displayedPanelMode}
        leftLabel="Joints"
        leftValue={PANEL_MODES.JOINTS}
        rightLabel="Actuators"
        rightValue={PANEL_MODES.ACTUATORS}
        onChange={setPanelMode}
      />
    </div>
    {displayedPanelMode === PANEL_MODES.ACTUATORS && !isRunning && (
      <div className={warningBannerClass}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yellow-300/90" />
        <span>
          Physics is off - this is a preview only. Turn on physics to see the real effect of
          actuators.
        </span>
      </div>
    )}
    {displayedPanelMode === PANEL_MODES.JOINTS ? (
      <PhysicsAuthoringLockNotice actionText={JOINT_TAB_PHYSICS_ON_NOTICE} />
    ) : null}
  </>
);

export { CloseButton };
export default ActuatorControlsPanelBody;
